/* @flow */

import { default as moment } from "moment";
import { Map } from "immutable";
import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import SitecoreStoreInformationActions from "../actions/SitecoreStoreInformationActions";
import ContentService from "../clients/ContentServiceClient";
import TrackingUtil from "shared/src/utils/TrackingUtil";
import TimeUtil from "../utils/TimeUtil";
import { DayName } from "../utils/DateUtil";

import { Store } from "shared/src/stores/Store";

export type StoreOpeningHours = {
    closedTo: string;
    closedFrom: string;
    openTo: string;
    openFrom: string;
    day: DayName;
};

export type StoreInformation = {
    storeId: string;
    sitecoreStoreId: string;
    city: string;
    contactEmail: string;
    contactPhone: string;
    // Ordered from Monday through Sunday
    openingHours: Array<StoreOpeningHours>;
    storeInformationUrl: string;
};

export type State = {
    isLoading: boolean;
    lastLoadTimestamp: any;
    sitecoreData: Map<string, StoreInformation>;
};

let state: State;

class SitecoreStoreInformation extends Store {
    constructor() {
        super();
        state = this.getEmptyState();
        this.listenTo(SitecoreStoreInformationActions.loadStoreInformation, this.loadStoreInformation);
        this.listenTo(SessionStore, this.loadStoreInformation);
        this.loadStoreInformation();
    }

    loadStoreInformation() {
        if (!SessionStore.isMarioCountry()) {
            return;
        }

        const currentCountry = SessionStore.getCountry();
        const currentLocale = SessionStore.getLocale();

        // do not load new data, if a loading is in progress or if the last load timestamp is not older than 1 hour
        if (state.isLoading || !TimeUtil.moreThanAmountOfMinutesAgo(state.lastLoadTimestamp, 60)) {
            return;
        }

        state.isLoading = true;
        ContentService.getSitecoreStoreInformation(currentCountry, currentLocale)
            .then((r) => this.loadStoreInformationSuccess(r))
            .catch((e: any) => this.loadStoreInformationError(e));
    }

    loadStoreInformationSuccess(response: { [x: string]: any }) {
        state.isLoading = false;
        state.lastLoadTimestamp = moment();
        state.sitecoreData = Map<string, StoreInformation>().withMutations((m) => {
            for (const [key, value] of Object.entries(response.storeInformationMap)) {
                m.set(key, value as StoreInformation);
            }
        });
        this.trigger();
    }

    loadStoreInformationError(e: any) {
        state = this.getEmptyState();
        TrackingUtil.trackUiErrorSd("SitecoreStoreInformation", "Loading of store information failed.", e);
        this.trigger();
    }

    getEmptyState(): State {
        return {
            isLoading: false,
            lastLoadTimestamp: undefined,
            sitecoreData: Map()
        };
    }

    getState(): State {
        return state;
    }

    // For tests
    resetState() {
        state = this.getEmptyState();
    }
}

export default new SitecoreStoreInformation();
